import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock, resolveComponent as _resolveComponent, withCtx as _withCtx } from "vue"

import { ChatSocketService } from "@/sockets/chat.socket";
import { UserSocketService } from "@/sockets/user.socket";
import { useAuthStore } from "@/stores/auth";
import { onMounted, watch } from "vue";
import { useChat } from "@/composition/chat/useChat";
import { useUserStore } from "@/stores/user";
import { useRoute } from "vue-router";
import { useTheme } from "@/composition/general/useTheme";
import { useEventsBus } from "@/composition/event/useEventBus";
import { EventsEnum } from "@/utils/enum/general/eventsEnum";
import { useWalletsStore } from "@/stores/wallets";
import { useSecurityStore } from "@/stores/security";
import { useKycStore } from "@/stores/kyc";
import { useSubscriptionStore } from "@/stores/subscription";
import { useUtilStore } from "@/stores/util";
import { useNotificationsStore } from "@/stores/notifications";
import { useLanguageStore } from "@/stores/language";
import { LanguageEnum } from "@/utils/enum/general/languageEnum";
import { Wallet } from "@/types/wallet/Wallet";
import { useNavigationStore } from "@/stores/navigation";
import { useBusinessStore } from "@/stores/business";


export default /*#__PURE__*/_defineComponent({
  __name: 'RootStack',
  setup(__props) {

const { getAmountAllNotReadMessages, setCurrentChat, receiveChatNotification } =
  useChat();

const { emit } = useEventsBus();
const user = useUserStore();
const language = useLanguageStore();
const subscription = useSubscriptionStore();
const security = useSecurityStore();
const wallet = useWalletsStore();
const kyc = useKycStore();
const notifications = useNotificationsStore();
const route = useRoute();
const business = useBusinessStore();
const { appointTheme, setTheme } = useTheme();

const auth = useAuthStore();
const utilStore = useUtilStore();
const navigation = useNavigationStore();

const connectUserSocketService = async () => {
  UserSocketService.getInstance();
  await UserSocketService.connect({ bearer: auth.getAccessToken });
};

const connectChatSocketService = async () => {
  ChatSocketService.getInstance();
  await ChatSocketService.connect({ bearer: auth.getAccessToken });
  ChatSocketService.subscribeToChatNotifications({
    userId: user.getUserId || "",
    cb: (tick) => {
      receiveChatNotification(tick);
    },
  });
};

const initSocketsConnections = async () => {
  await Promise.all([connectUserSocketService(), connectChatSocketService()]);
  emit(EventsEnum.ROOT_SOCKETS_CONNECTIONS_ESTABLISHED);
  await Promise.all([
    subscribeToUserReceiveAppNotifications(),
    subscribeToUpdateBalances(),
  ]);
};

const subscribeToUserReceiveAppNotifications = async () => {
  UserSocketService.subscribeToUserReceiveAppNotifications({
    userId: user.userId || "",
    cb: (tick) => {
      notifications.increaseUnreadAppNotificationsQuantity();
      emit(EventsEnum.RECEIVE_APP_NOTIFICATION, { notification: tick });
    },
  });
};

const subscribeToUpdateBalances = async () => {
  UserSocketService.subscribeToUpdateBalances({
    userId: user.userId || "",
    cb: (tick) => {
      wallet.updateWallet(tick as Wallet);
    },
  });
};

const init = (fetchUserInfo = true) => {
  if (auth.getIsAuthorized) {
    fetchUserInfo && user.getUserInfo();
    wallet.getWallets();
    security.getSettings();
    kyc.getKyc();
    subscription.getSubscription();
    notifications.getNotificationsCount();
    business.getSubscribeInfo();
    getAmountAllNotReadMessages();
    initSocketsConnections();
  }
  setTheme(appointTheme());
  const lang =
    (localStorage.getItem("lang") as LanguageEnum) || route.params.lang || "";
  language.setLanguage(lang);
  language.reinstallI18n(lang);
  utilStore.getUsdPrices();
};

watch(
  () => auth.getIsAuthorized,
  async () => {
    init(false);
    if (!auth.getIsAuthorized) return;
    await user.getUserInfo();
    !user.getIsFullInformationEntered &&
      emit(EventsEnum.NEED_PROVIDE_PERSONAL_INFO);
  }
);
watch(
  () => business.isBusinessConnected,
  () => business.getSubscribeInfo()
);
watch(
  () => route.name,
  (value, oldValue) => {
    if (oldValue === "Dialog") {
      setCurrentChat(null);
    }
  }
);

onMounted(() => {
  init();
});

return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createBlock(_component_router_view, {
    name: "RootStackRouterView",
    key: _unref(navigation).rootStackRouteViewKey
  }, {
    default: _withCtx(({ Component }) => [
      (_openBlock(), _createBlock(_resolveDynamicComponent(Component)))
    ]),
    _: 1
  }))
}
}

})